/*
    MlbTeamLineups Component
    This component is used to display the team lineups data for a given game.

    it will display:
        - Starting Pitchers, with Confirmed Status - (either confirmed or projected)
            - Stats for pitchers - Record (Wins - Losses), ERA (EarnedRunAverage), K/9 (PitchingWeightedOnBasePercentage)
        - Batting Lineup - with confirmed status on the header (can be validaed if individual players have a confirmed status)
            - Display batting order, Player name, position and stats
            - Stats for batters - BA (BattingAverage), wOBA (WeightedOnBasePercentage), OPS (OnBasePlusSlugging)
    
    This component uses a unified comparative layout where both teams' stats are displayed together 
    in each section for easier comparison, with clear team identification. When on Mobile there should be a switch to toggle between the two teams..

    
    Props: 
        - game: the game object
        - sport: sport identifier (e.g., 'MLB')
        - gameLineups: dict with HomeTeamID, AwayTeamID, HomeStartingPitcher (Object), AwayStartingPitcher (Object), HomeBattingLineup (Array), AwayBattingLineup (Array) 
        - teamStats: object keyed by TeamID containing arrays of player stat objects
        - homeTeam: home team object
        - awayTeam: away team object
*/

import React, { useState } from 'react';

const MlbTeamLineups = ({ game, gameLineups, teamStats, homeTeamID, awayTeamID, homeTeamLabel = null, awayTeamLabel = null, onPlayerClick }) => {
    const [selectedTeam, setSelectedTeam] = useState('home'); // 'both', 'home', 'away'

    // Find depth charts for each team
    const homeStartingPitcher = gameLineups?.HomeStartingPitcher;
    const awayStartingPitcher = gameLineups?.AwayStartingPitcher;
    const homeBattingLineup = gameLineups?.HomeBattingLineup;
    const awayBattingLineup = gameLineups?.AwayBattingLineup;

    // Helper function to get player stats
    const getPlayerStats = (teamID, playerID) => {
        if (!teamStats || !teamStats[teamID]) return null;
        return teamStats[teamID].find(stat => stat.PlayerID === playerID);
    };

    // Helper function to format pitcher stats
    const formatPitcherStats = (stats) => {
        if (!stats) return { record: 'N/A', era: 'N/A', k9: 'N/A' };
        
        const wins = stats.Wins || 0;
        const losses = stats.Losses || 0;
        const era = stats.EarnedRunAverage?.toFixed(2) || 'N/A';
        const k9 = stats.PitchingStrikeoutsPerNineInnings?.toFixed(1) || 'N/A';
        
        return {
            record: `${wins}-${losses}`,
            era,
            k9
        };
    };

    // Helper function to format batter stats
    const formatBatterStats = (stats) => {
        if (!stats) return { ba: 'N/A', woba: 'N/A', ops: 'N/A' };
        
        const ba = stats.BattingAverage?.toFixed(3) || 'N/A';
        const woba = stats.WeightedOnBasePercentage?.toFixed(3) || 'N/A';
        const ops = stats.OnBasePlusSlugging?.toFixed(3) || 'N/A';
        
        return { ba, woba, ops };
    };

    // Render pitcher card
    const renderPitcherCard = (pitcher, teamID) => {
        if (!pitcher) {
            return (
                <div className="flex items-center justify-center px-4 py-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600">
                    <div className="text-center text-sm text-gray-500 dark:text-gray-400">
                        No pitcher confirmed
                    </div>
                </div>
            );
        }

        const stats = getPlayerStats(teamID, pitcher.PlayerID);
        const { record, era, k9 } = formatPitcherStats(stats);
        const confirmed = pitcher.Confirmed ? 'Confirmed' : 'Projected';

        return (
            <div 
                className="px-4 py-3 rounded-lg bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                onClick={() => onPlayerClick?.(pitcher, teamID, 'pitcher')}
            >
                <div className="flex items-center justify-center mb-2">
                    <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                            {pitcher.FirstName} {pitcher.LastName}
                        </span>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${
                            confirmed === 'Confirmed' 
                                ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300 border-green-300 dark:border-green-700'
                                : 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 border-yellow-300 dark:border-yellow-700'
                        }`}>
                            {confirmed}
                        </span>
                    </div>
                </div>
                <div className="flex justify-center gap-4 text-xs">
                    <div>
                        <div className="text-gray-500 dark:text-gray-400">Record</div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">{record}</div>
                    </div>
                    <div>
                        <div className="text-gray-500 dark:text-gray-400">ERA</div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">{era}</div>
                    </div>
                    <div>
                        <div className="text-gray-500 dark:text-gray-400">K/9</div>
                        <div className="font-medium text-gray-900 dark:text-gray-100">{k9}</div>
                    </div>
                </div>
            </div>
        );
    };

    // Render batter row in lineup
    const renderBatterRow = (batter, teamID) => {
        if (!batter) return null;

        const stats = getPlayerStats(teamID, batter.PlayerID);
        const { ba, woba, ops } = formatBatterStats(stats);

        return (
            <div 
                key={batter.PlayerID}
                className="px-3 py-2 rounded-md bg-gray-50 dark:bg-gray-700/50 border border-gray-200 dark:border-gray-600 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                onClick={() => onPlayerClick?.(batter, teamID, 'batter')}
            >
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-1 min-w-0">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 w-4">
                            {batter.BattingOrder}
                        </span>
                        <div className="flex flex-col">
                            <span className="text-sm text-gray-900 dark:text-gray-100">
                                {batter.FirstName} {batter.LastName}
                            </span>
                            <span className="text-[10px] text-gray-500 dark:text-gray-400">
                                {batter.Position}
                            </span>
                        </div>
                    </div>
                    <div className="flex gap-2 text-xs">
                        <div className="text-right min-w-[3rem]">
                            <div className="text-gray-500 dark:text-gray-400 text-[10px]">BA</div>
                            <div className="font-medium text-gray-900 dark:text-gray-100">{ba}</div>
                        </div>
                        <div className="text-right min-w-[3rem]">
                            <div className="text-gray-500 dark:text-gray-400 text-[10px]">wOBA</div>
                            <div className="font-medium text-gray-900 dark:text-gray-100">{woba}</div>
                        </div>
                        <div className="text-right min-w-[3rem]">
                            <div className="text-gray-500 dark:text-gray-400 text-[10px]">OPS</div>
                            <div className="font-medium text-gray-900 dark:text-gray-100">{ops}</div>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // Check if any lineup data exists
    const hasLineupData = homeStartingPitcher || awayStartingPitcher || 
                          (homeBattingLineup && homeBattingLineup.length > 0) || 
                          (awayBattingLineup && awayBattingLineup.length > 0);

    if (!hasLineupData) {
        return (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                No lineup data available
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600 rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center justify-between mb-4 relative mt-1">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Team Lineups</h3>
            </div>
            
            {/* Starting Pitchers Section */}
            {(homeStartingPitcher || awayStartingPitcher) && (
                <div className="mb-6">
                    {/* Desktop: Comparative Layout */}
                    <div className="hidden lg:grid lg:grid-cols-2 lg:gap-4 my-8">
                        <div>
                            {renderPitcherCard(awayStartingPitcher, awayTeamID, awayTeamLabel)}
                        </div>
                        <div>
                            {renderPitcherCard(homeStartingPitcher, homeTeamID, homeTeamLabel)}
                        </div>
                    </div>

                    {/* Mobile: Toggle Layout */}
                    <div className="block lg:hidden">
                        <div className="flex gap-2 mb-4">
                            <button
                                onClick={() => setSelectedTeam('away')}
                                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                                    selectedTeam === 'away'
                                        ? 'bg-gray-700 dark:bg-gray-600 text-white'
                                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                }`}
                            >
                                {awayTeamLabel}
                            </button>
                            <button
                                onClick={() => setSelectedTeam('home')}
                                className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                                    selectedTeam === 'home'
                                        ? 'bg-gray-700 dark:bg-gray-600 text-white'
                                        : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                }`}
                            >
                                {homeTeamLabel}
                            </button>
                        </div>

                        {selectedTeam === 'away' && renderPitcherCard(awayStartingPitcher, awayTeamID, awayTeamLabel)}
                        {selectedTeam === 'home' && renderPitcherCard(homeStartingPitcher, homeTeamID, homeTeamLabel)}
                    </div>
                </div>
            )}

            {/* Batting Lineup Section */}
            {((homeBattingLineup && homeBattingLineup.length > 0) || (awayBattingLineup && awayBattingLineup.length > 0)) && (
                <div className="mb-6">
                    <h3 className="flex items-center justify-center text-base font-semibold text-gray-900 dark:text-gray-100 mb-4 pb-2 border-b border-gray-200 dark:border-gray-600">
                        Batting Lineups
                    </h3>
                    
                    {/* Desktop: Comparative Layout */}
                    <div className="hidden lg:block">
                        <div className="grid grid-cols-2 gap-4 mb-4">
                            <div className="text-center">
                                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    {awayTeamLabel} (Away)
                                </span>
                            </div>
                            <div className="text-center">
                                <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                                    {homeTeamLabel} (Home)
                                </span>
                            </div>
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            {/* Away Team Lineup */}
                            <div className="space-y-1">
                                {awayBattingLineup && awayBattingLineup.length > 0 ? (
                                    awayBattingLineup.map(batter => renderBatterRow(batter, awayTeamID))
                                ) : (
                                    <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
                                        Lineup not available
                                    </div>
                                )}
                            </div>

                            {/* Home Team Lineup */}
                            <div className="space-y-1">
                                {homeBattingLineup && homeBattingLineup.length > 0 ? (
                                    homeBattingLineup.map(batter => renderBatterRow(batter, homeTeamID))
                                ) : (
                                    <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
                                        Lineup not available
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Mobile: Toggle Layout */}
                    <div className="block lg:hidden">
                        {!((homeStartingPitcher || awayStartingPitcher)) && (
                            <div className="flex gap-2 mb-4">
                                <button
                                    onClick={() => setSelectedTeam('away')}
                                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                                        selectedTeam === 'away'
                                            ? 'bg-gray-700 dark:bg-gray-600 text-white'
                                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                    }`}
                                >
                                    {awayTeamLabel}
                                </button>
                                <button
                                    onClick={() => setSelectedTeam('home')}
                                    className={`flex-1 py-2 px-4 rounded-md text-sm font-medium transition-colors ${
                                        selectedTeam === 'home'
                                            ? 'bg-gray-700 dark:bg-gray-600 text-white'
                                            : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                                    }`}
                                >
                                    {homeTeamLabel}
                                </button>
                            </div>
                        )}

                        {selectedTeam === 'away' && (
                            <div className="space-y-1">
                                {awayBattingLineup && awayBattingLineup.length > 0 ? (
                                    awayBattingLineup.map(batter => renderBatterRow(batter, awayTeamID))
                                ) : (
                                    <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
                                        Lineup not available
                                    </div>
                                )}
                            </div>
                        )}

                        {selectedTeam === 'home' && (
                            <div className="space-y-1">
                                {homeBattingLineup && homeBattingLineup.length > 0 ? (
                                    homeBattingLineup.map(batter => renderBatterRow(batter, homeTeamID))
                                ) : (
                                    <div className="text-center py-4 text-sm text-gray-500 dark:text-gray-400">
                                        Lineup not available
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

export default MlbTeamLineups;